import { useMutation } from "@apollo/client";
import { ChangeEventHandler, useEffect, useState } from "react";
import {
  GetAccountQuery,
  UpdateUserSettingsMutation,
  UpdateUserSettingsMutationVariables,
  UserMeetingBotPreference,
  UserVideoRecordingPreference,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Heading from "@components/heading/heading";
import SettingsLayout from "@components/settings-layout/settings-layout";

import updateUserMutation from "../graphql/update-user-mutation";

const notetakerOptions = [
  {
    value: UserMeetingBotPreference.WhenParticipating,
    label: "All My Meetings",
  },
  {
    value: UserMeetingBotPreference.WhenOrganizer,
    label: "All Meetings Organized by Me",
  },
  {
    value: UserMeetingBotPreference.UseMeetingSetting,
    label: "Only The Meetings I select",
  },
];

const videoRecordingOptions = [
  {
    value: UserVideoRecordingPreference.Never,
    label: "Never Automatically Save",
  },
  {
    value: UserVideoRecordingPreference.WhenOrganizer,
    label: "Meetings I Organize",
  },
  {
    value: UserVideoRecordingPreference.WhenParticipating,
    label: "All Meetings I Join",
  },
];

const AccountMeetings = ({ me }: { me: GetAccountQuery["me"] }) => {
  const currentUser = currentUserVar();
  const userId = me?.id ?? currentUser.id;

  const [notetakerPreference, setNotetakerPreference] =
    useState<UserMeetingBotPreference>(
      me?.meetingBotPreference ??
        currentUser.meetingBotPreference ??
        UserMeetingBotPreference.UseMeetingSetting,
    );
  const [videoPreference, setVideoPreference] =
    useState<UserVideoRecordingPreference>(
      me?.videoRecordingPreference ??
        currentUser.videoRecordingPreference ??
        UserVideoRecordingPreference.Never,
    );

  useEffect(() => {
    if (
      me?.meetingBotPreference !== undefined &&
      me?.meetingBotPreference !== null
    ) {
      setNotetakerPreference(me.meetingBotPreference);
    }
  }, [me?.meetingBotPreference]);

  useEffect(() => {
    if (
      me?.videoRecordingPreference !== undefined &&
      me?.videoRecordingPreference !== null
    ) {
      setVideoPreference(me.videoRecordingPreference);
    }
  }, [me?.videoRecordingPreference]);

  const [updateUser] = useMutation<
    UpdateUserSettingsMutation,
    UpdateUserSettingsMutationVariables
  >(updateUserMutation);

  const handleChangeNotetakerSettings: ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    updateUser({
      variables: {
        userId,
        meetingBotPreference: e.target.value as UserMeetingBotPreference,
      },
    });
    const updatedUser = currentUserVar();
    currentUserVar({
      ...updatedUser,
      meetingBotPreference: e.target.value as UserMeetingBotPreference,
    });
    setNotetakerPreference(e.target.value as UserMeetingBotPreference);
  };

  const handleChangeVideoRecordingSettings: ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    updateUser({
      variables: {
        userId,
        videoRecordingPreference: e.target
          .value as UserVideoRecordingPreference,
      },
    });
    const updatedUser = currentUserVar();
    currentUserVar({
      ...updatedUser,
      videoRecordingPreference: e.target.value as UserVideoRecordingPreference,
    });
    setVideoPreference(e.target.value as UserVideoRecordingPreference);
  };

  return (
    <div>
      <Heading small title="Meeting Preferences" />

      <SettingsLayout.Container>
        <SettingsLayout.Row>
          <SettingsLayout.Left>
            Topicflow Notetaker Will Join
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="flex flex-col">
              {notetakerOptions.map(({ value, label }) => (
                <div
                  key={value}
                  className="text-sm text-gray-800 flex items-center py-1"
                >
                  <input
                    type="radio"
                    name="link-option"
                    id={`input-link-option-${value}`}
                    value={value}
                    checked={notetakerPreference === value}
                    onChange={handleChangeNotetakerSettings}
                  />
                  <label
                    className="ml-2 cursor-pointer"
                    htmlFor={`input-link-option-${value}`}
                  >
                    {label}
                  </label>
                </div>
              ))}
              <div className="text-gray-500 text-sm mt-2">
                You can manually override this setting for each individual
                meeting.
              </div>
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>
        <SettingsLayout.Row>
          <SettingsLayout.Left>
            Save Meeting Video Recordings
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="flex flex-col">
              {videoRecordingOptions.map(({ value, label }) => (
                <div
                  key={value}
                  className="text-sm text-gray-800 flex items-center py-1"
                >
                  <input
                    type="radio"
                    name="video-recording-option"
                    id={`input-video-recording-option-${value}`}
                    value={value}
                    checked={videoPreference === value}
                    onChange={handleChangeVideoRecordingSettings}
                  />
                  <label
                    className="ml-2 cursor-pointer"
                    htmlFor={`input-video-recording-option-${value}`}
                  >
                    {label}
                  </label>
                </div>
              ))}
              <div className="text-gray-500 text-sm mt-2">
                You can manually save meeting video recordings for up to seven
                days after the meeting
              </div>
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>
      </SettingsLayout.Container>
    </div>
  );
};

export default AccountMeetings;
